package or.garden.manager.features.beds

import androidx.compose.runtime.*
import karika.distribucija.ba.domain.model.ResultState
import or.garden.manager.components.CardSkeleton
import or.garden.manager.components.SnackbarType
import or.garden.manager.domain.PlantedCropService
import or.garden.manager.domain.model.*
import or.garden.manager.features.beds.components.BedPlannerView
import or.garden.manager.features.beds.components.BedsCard
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.Text

@Composable
fun BedsScreen(
    beds: List<BedData>,
    isLoadingBeds: Boolean,
    crops: List<CropData>,
    onRefreshBeds: () -> Unit,
    onEditBed: (BedData) -> Unit,
    onDeleteBed: (Int) -> Unit,
    showSnackbar: (String, SnackbarType) -> Unit,
    onPlannerStateChange: (
        isOpen: Boolean,
        onSave: (() -> Unit)?,
        onAddCrop: (() -> Unit)?,
        onAiGenerate: (() -> Unit)?,
        onDeleteAll: (() -> Unit)?,
        isSaving: Boolean,
        hasPlantedCrops: Boolean
    ) -> Unit = { _, _, _, _, _, _, _ -> }
) {
    var selectedBedForPlanner by remember { mutableStateOf<BedData?>(null) }
    var plantedCrops by remember { mutableStateOf<List<PlantedCrop>>(emptyList()) }
    var aiSuggestions by remember { mutableStateOf<PlantingSuggestionsResponse?>(null) }
    var isLoadingPlantedCrops by remember { mutableStateOf(false) }
    var isSavingPlanner by remember { mutableStateOf(false) }
    var showAiModal by remember { mutableStateOf(false) }
    var showAddCropModal by remember { mutableStateOf(false) }

    fun fetchPlantedCrops(bedId: Int) {
        isLoadingPlantedCrops = true
        PlantedCropService.getPlantedCrops(bedId) { state ->
            when (state) {
                is ResultState.Success -> {
                    val data = state.data.data
                    plantedCrops = data?.plantedCrops ?: emptyList()
                    // Map PlantingSuggestionsResponse to the state if available
                    aiSuggestions = data
                    isLoadingPlantedCrops = false
                }
                is ResultState.Error -> {
                    console.error("Greška pri učitavanju zasađenih kultura: ${state.message}")
                    isLoadingPlantedCrops = false
                }
                else -> {}
            }
        }
    }

    fun generateAiLayout(selectedCrops: List<CropData>, includePathways: Boolean, pathwayWidth: Int) {
        val bed = selectedBedForPlanner ?: return
        isLoadingPlantedCrops = true
        val request = GroqRequest(
            crops = selectedCrops,
            bed = bed,
            includePathways = includePathways,
            pathwayWidth = pathwayWidth
        )
        
        PlantedCropService.getGroqSuggestions(request) { state ->
            when (state) {
                is ResultState.Success -> {
                    plantedCrops = state.data.plantedCrops
                    aiSuggestions = state.data
                    isLoadingPlantedCrops = false
                }
                is ResultState.Error -> {
                    showSnackbar("AI Greška: ${state.message}", SnackbarType.ERROR)
                    isLoadingPlantedCrops = false
                }
                else -> {}
            }
        }
    }

    fun savePlannerLayout() {
        val bed = selectedBedForPlanner ?: return
        isSavingPlanner = true
        val requests = plantedCrops.map {
            val isPath = it.crop?.icon == "pathway"
            val isFoil = it.crop?.icon == "strawberry_foil"
            val isRotated = bed.orientation == "1"
            
            // Normalize cropId: Use a real database ID (> 0) if it's a special type.
            // Currently, pathways and foils are using -1, -2, -3 which fail DB foreign key constraints.
            // Based on server setup, these should probably be mapped to their actual crop ID in the DB.
            // For now, if it's < 0, we'll try to find a matching crop in 'availableCrops' (crops) by icon.
            val effectiveCropId = if ((it.crop?.id ?: 0) < 0) {
                crops.find { c -> c.icon == it.crop?.icon }?.id ?: 0
            } else {
                it.crop?.id ?: 0
            }

            CreatePlantedCropRequest(
                cropId = effectiveCropId,
                positionX = it.positionX,
                positionY = it.positionY,
                plantingDate = it.plantingDate,
                foilId = it.foilId,
                tempId = it.id,
                customWidth = when {
                    isPath -> {
                        if (isRotated) (it.customWidth ?: it.crop.spacingBetweenPlants)
                        else if (it.positionX == 0) bed.width 
                        else (it.customWidth ?: it.crop.spacingBetweenPlants)
                    }
                    isFoil -> {
                        if (isRotated) (it.customWidth ?: 50)
                        else if (it.positionX == 0) bed.width
                        else 50
                    }
                    else -> it.customWidth
                },
                customHeight = when {
                    isPath -> {
                        if (isRotated) bed.length
                        else if (it.positionX == 0) (it.customHeight ?: it.crop.spacingBetweenPlants)
                        else bed.length
                    }
                    isFoil -> {
                        if (isRotated) bed.length
                        else if (it.positionX == 0) 50
                        else bed.length
                    }
                    else -> it.customHeight
                },
                holeSpacing = if (isFoil) it.holeSpacing else null,
                rows = if (isFoil) it.rows else null
            )
        }

        val aiSuggestionsData = aiSuggestions?.let {
            AiSuggestionsData(
                layoutGuidelines = it.layoutGuidelines,
                rotationPlan = it.rotationPlan,
                practicalAdvice = it.practicalAdvice
            )
        }

        PlantedCropService.replacePlantedCrops(bed.id, requests, aiSuggestionsData) { state ->
            isSavingPlanner = false
            if (state is ResultState.Success) {
                showSnackbar("Plan uspješno sačuvan!", SnackbarType.SUCCESS)
                onRefreshBeds() // Trigger refresh
            } else if (state is ResultState.Error) {
                showSnackbar("Greška pri spremanju: ${state.message}", SnackbarType.ERROR)
            }
        }
    }

    if (selectedBedForPlanner != null) {
        // Create a reference to the BedPlannerView controller if needed,
        // but for now we'll just use a state-based trigger.
        var showDeleteAllTrigger by remember { mutableStateOf(false) }

        // We need to pass the onDeleteAll to the parent, but also handle it here if we want.
        // Actually, the parent (Dashboard) has the button.
        // When it clicks the button, it calls the onDeleteAll we passed in onPlannerStateChange.
        // So we should pass a function that triggers showDeleteAllModal inside BedPlannerView.

        LaunchedEffect(selectedBedForPlanner, isSavingPlanner, plantedCrops.size, showAiModal, showAddCropModal) {
            if (selectedBedForPlanner != null) {
                onPlannerStateChange(
                    true,
                    { savePlannerLayout() },
                    { showAddCropModal = true },
                    { showAiModal = true },
                    { showDeleteAllTrigger = true },
                    isSavingPlanner,
                    plantedCrops.isNotEmpty()
                )
            } else {
                onPlannerStateChange(false, null, null, null, null, false, false)
            }
        }

        BedPlannerView(
            bed = selectedBedForPlanner!!,
            plantedCrops = plantedCrops,
            availableCrops = crops,
            isLoading = isLoadingPlantedCrops,
            isSaving = isSavingPlanner,
            aiSuggestions = aiSuggestions,
            onBack = { selectedBedForPlanner = null; aiSuggestions = null },
            onSave = { savePlannerLayout() },
            showAiModalState = showAiModal,
            onShowAiModalChange = { showAiModal = it },
            showAddCropModalState = showAddCropModal,
            onShowAddCropModalChange = { showAddCropModal = it },
            showDeleteAllModalState = showDeleteAllTrigger,
            onShowDeleteAllModalChange = { showDeleteAllTrigger = it },
            onDeleteAll = { showDeleteAllTrigger = false },
            onAddCrop = { crop: CropData, x: Int, y: Int, explicitFoilId: Int? ->
                val now = kotlin.js.Date()
                val year = now.getFullYear()
                val month = (now.getMonth() + 1).toString().padStart(2, '0')
                val day = now.getDate().toString().padStart(2, '0')
                val isoDate = "$year-$month-$day"

                // Use explicitFoilId if provided, otherwise fallback to coordinate-based detection
                val foilId = explicitFoilId ?: plantedCrops.find { foil ->
                    if (foil.crop?.icon != "strawberry_foil") return@find false
                    val fWidth = if (foil.positionX == 0) selectedBedForPlanner!!.width else (foil.customWidth ?: 50)
                    val fHeight = if (foil.positionX == 0) (foil.customHeight ?: 50) else selectedBedForPlanner!!.length
                    
                    x >= foil.positionX && x <= foil.positionX + fWidth &&
                    y >= foil.positionY && y <= foil.positionY + fHeight
                }?.id

                // USE A MORE ROBUST POSITIVE-HASHED TIMESTAMP TO ENSURE IT FITS IN INT
                // We use negative to distinguish from DB IDs, but must ensure it's within Int range
                val timestamp = kotlin.js.Date().getTime().toDouble()
                val newId = -(timestamp.rem(1000000000.0).toInt())
                
                // CRITICAL: Need to ensure newId is unique if multiple are added in a single tick (like Fill Foil)
                // The loop in BedPlannerView might call this very fast.
                val uniqueId = if (plantedCrops.any { it.id == newId }) {
                    newId - (plantedCrops.size + 1)
                } else {
                    newId
                }

                val isPath = crop.id == -1 || crop.icon == "pathway"
                val isFoil = crop.id == -2 || crop.icon == "strawberry_foil"
                val isRotated = selectedBedForPlanner!!.orientation == "1"

                val finalCustomWidth = when {
                    isPath -> {
                        if (isRotated) (crop.spacingBetweenPlants)
                        else if (x == 0) selectedBedForPlanner!!.width
                        else crop.spacingBetweenPlants
                    }
                    isFoil -> {
                        if (isRotated) 50
                        else if (x == 0) selectedBedForPlanner!!.width
                        else 50
                    }
                    else -> null
                }
                
                val finalCustomHeight = when {
                    isPath -> {
                        if (isRotated) selectedBedForPlanner!!.length
                        else if (x == 0) crop.spacingBetweenPlants
                        else selectedBedForPlanner!!.length
                    }
                    isFoil -> {
                        if (isRotated) selectedBedForPlanner!!.length
                        else if (x == 0) 50
                        else selectedBedForPlanner!!.length
                    }
                    else -> null
                }

                val newPlanted = PlantedCrop(
                    id = uniqueId,
                    bedId = selectedBedForPlanner!!.id,
                    crop = crop,
                    positionX = x,
                    positionY = y,
                    plantingDate = isoDate,
                    foilId = foilId,
                    customWidth = finalCustomWidth,
                    customHeight = finalCustomHeight,
                    holeSpacing = if (isFoil) crop.holeSpacing else null,
                    rows = if (isFoil) crop.rows else null
                )
                plantedCrops = plantedCrops + newPlanted
            },
            onRemoveCrop = { id: Int ->
                val toRemove = plantedCrops.find { it.id == id }
                if (toRemove?.crop?.icon == "strawberry_foil") {
                    // CASCADING DELETE: Remove the foil and all crops linked to it
                    plantedCrops = plantedCrops.filter { it.id != id && it.foilId != id }
                } else {
                    plantedCrops = plantedCrops.filter { it.id != id }
                }
            },
            onMoveCrop = { id: Int, x: Int, y: Int ->
                val dragged = plantedCrops.find { it.id == id }
                if (dragged?.crop?.icon == "strawberry_foil") {
                    val dx = x - dragged.positionX
                    val dy = y - dragged.positionY
                    
                    val draggedWidth = if (dragged.positionX == 0) selectedBedForPlanner!!.width else (dragged.customWidth ?: 50)
                    val draggedHeight = if (dragged.positionX == 0) (dragged.customHeight ?: 50) else selectedBedForPlanner!!.length
                    val holeSpacing = dragged.crop.spacingBetweenPlants ?: 30

                    if (dragged.positionX == 0) {
                        val holeCount = (draggedWidth / holeSpacing).coerceAtLeast(1)
                        val horizontalPadding = (draggedWidth % holeSpacing) / 2

                        val holePositions = mutableSetOf<Pair<Int, Int>>()
                        for (index in 0 until holeCount) {
                            val centerX = horizontalPadding + (index * holeSpacing) + (holeSpacing/2)
                            holePositions.add((dragged.positionX + centerX) to (dragged.positionY + (draggedHeight * 0.25).toInt()))
                            holePositions.add((dragged.positionX + centerX) to (dragged.positionY + (draggedHeight * 0.75).toInt()))
                        }

                        // Also move children based on the dx/dy
                        plantedCrops = plantedCrops.map {
                            if (it.id == id) it.copy(positionX = x, positionY = y)
                            else if (it.foilId == id) it.copy(positionX = it.positionX + dx, positionY = it.positionY + dy)
                            else it
                        }
                    } else {
                        val holeCount = (draggedHeight / holeSpacing).coerceAtLeast(1)
                        val verticalPadding = (draggedHeight % holeSpacing) / 2

                        val holePositions = mutableSetOf<Pair<Int, Int>>()
                        for (index in 0 until holeCount) {
                            val centerY = verticalPadding + (index * holeSpacing) + (holeSpacing/2)
                            holePositions.add((dragged.positionX + (draggedWidth * 0.25).toInt()) to (dragged.positionY + centerY))
                            holePositions.add((dragged.positionX + (draggedWidth * 0.75).toInt()) to (dragged.positionY + centerY))
                        }

                        // Move children
                        plantedCrops = plantedCrops.map {
                            if (it.id == id) it.copy(positionX = x, positionY = y)
                            else if (it.foilId == id) it.copy(positionX = it.positionX + dx, positionY = it.positionY + dy)
                            else it
                        }
                    }
                } else if (dragged != null) {
                    plantedCrops = plantedCrops.map {
                        if (it.id == id) it.copy(positionX = x, positionY = y) else it
                    }
                }
            },
            onGenerateAi = { selectedCrops, includePathways, width ->
                generateAiLayout(selectedCrops, includePathways, width)
            }
        )
    } else if (isLoadingBeds) {
        Div({
            classes("grid", "grid-cols-1", "md:grid-cols-2", "lg:grid-cols-3", "gap-4", "md:gap-8")
        }) {
            repeat(3) { CardSkeleton() }
        }
    } else if (beds.isEmpty()) {
        Div({ classes("text-center", "py-20", "text-gray-400") }) {
            I({ classes("fas", "fa-seedling", "text-4xl", "mb-4") })
            H2 { Text("Nisu pronađene leje. Kreirajte svoju prvu leju!") }
        }
    } else {
        Div({
            classes("grid", "grid-cols-1", "md:grid-cols-2", "lg:grid-cols-3", "gap-4", "md:gap-8")
        }) {
            beds.forEach { bed ->
                BedsCard(
                    name = bed.name,
                    size = "${bed.width}cm x ${bed.length}cm",
                    status = bed.type,
                    onSettingsClick = { onEditBed(bed) },
                    onPlannerClick = {
                        selectedBedForPlanner = bed
                        fetchPlantedCrops(bed.id)
                    },
                    onDeleteClick = { onDeleteBed(bed.id) }
                )
            }
        }
    }
}
